import { useEffect, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';

import { spanText } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { Meter } from '../../shared/ui/meter';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import { Note } from '../../shared/ui/note';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  stopBulkAfterBatch,
  useBulkOperation,
  type TBulkOperation,
} from '../../entities/bulk-operation';
import {
  previewFlowRedeliver,
  runKeys,
  startFlowRedeliver,
  type IRedeliverScope,
} from '../../entities/run';

const count = (value: number) => value.toLocaleString('en-US');
const copies = (value: number) => `${count(value)} failed ${value === 1 ? 'copy' : 'copies'}`;

interface IStarted {
  flowId: string;
  operationId: string;
}

interface IFlowRedeliverProps {
  /** One per flow: the API redelivers a flow's copies at a time, so several flows are several jobs. */
  scopes: readonly IRedeliverScope[];
  flowName: (flowId: string) => string;
  /** "at Billing · to Billing API": what the filters narrowed it to, in words. */
  where: string;
}

// One job's progress; reports its end so the dialog can say when all of them are done.
const JobRow = ({
  started,
  name,
  showName,
  onFinished,
}: {
  started: IStarted;
  name: string;
  showName: boolean;
  onFinished: (operationId: string, operation: TBulkOperation) => void;
}) => {
  const { operation, finished } = useBulkOperation(started.operationId);
  useEffect(() => {
    if (operation && finished) onFinished(started.operationId, operation);
  }, [operation, finished, onFinished, started.operationId]);

  const processed = operation?.processed ?? 0;
  const all = operation?.total ?? 0;
  return (
    <div className="flex flex-col gap-2">
      {showName && <p className="text-meta text-primary">{name}</p>}
      {!operation && <LoadingState rows={1} />}
      <Meter value={all > 0 ? (processed / all) * 100 : 0} tone="success" />
      {operation && (
        <p className="font-mono text-micro text-faint">
          {count(operation.succeeded)} sent again · {count(operation.failed)} could not be sent
          {operation.etaMs && !finished ? ` · about ${spanText(operation.etaMs)} left` : ''}
        </p>
      )}
      {operation?.error && <Note tone="danger">{operation.error}</Note>}
    </div>
  );
};

// Main's "Fill in what was missed" for flows: every copy that failed at the step in the window,
// sent again at that step by a server job (the issue page's Redeliver, over a window instead).
export const FlowRedeliver = ({ scopes, flowName, where }: IFlowRedeliverProps) => {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState<IStarted[]>([]);
  const [done, setDone] = useState<Record<string, TBulkOperation>>({});

  const previews = useQueries({
    queries: scopes.map((scope) => ({
      queryKey: ['flow-redeliver-preview', scope],
      queryFn: () => previewFlowRedeliver(scope),
    })),
  });
  const counted = scopes
    .map((scope, index) => ({ scope, total: previews[index]?.data?.total ?? 0 }))
    .filter((entry) => entry.total > 0);
  const total = counted.reduce((sum, entry) => sum + entry.total, 0);
  const failedPreview = previews.find((preview) => preview.isError);
  const notes = [...new Set(previews.flatMap((preview) => preview.data?.notes ?? []))];

  const start = useMutation({
    mutationFn: () =>
      Promise.all(
        counted.map(async ({ scope }) => ({
          flowId: scope.flowId,
          operationId: (await startFlowRedeliver(scope)).id,
        })),
      ),
    onSuccess: setStarted,
  });
  const stop = useMutation({
    mutationFn: () =>
      Promise.all(
        started
          .filter((job) => !done[job.operationId])
          .map((job) => stopBulkAfterBatch(job.operationId)),
      ),
  });
  const onFinished = (operationId: string, operation: TBulkOperation) =>
    setDone((current) =>
      current[operationId] === operation ? current : { ...current, [operationId]: operation },
    );

  const close = (next: boolean) => {
    if (!next) {
      setStarted([]);
      setDone({});
      start.reset();
      stop.reset();
      void client.invalidateQueries({ queryKey: ['flow-redeliver-preview'] });
      void client.invalidateQueries({ queryKey: runKeys.all });
    }
    setOpen(next);
  };

  if (total === 0) return null;
  const several = counted.length > 1;
  const finishedAll = started.length > 0 && started.every((job) => done[job.operationId]);
  const sent = Object.values(done).reduce((sum, operation) => sum + operation.succeeded, 0);
  const refused = Object.values(done).reduce((sum, operation) => sum + operation.failed, 0);

  const jobs = started.map((job) => (
    <JobRow
      key={job.operationId}
      started={job}
      name={flowName(job.flowId)}
      showName={started.length > 1}
      onFinished={onFinished}
    />
  ));

  const body = () => {
    if (finishedAll) {
      return (
        <Modal
          open={open}
          onOpenChange={close}
          title={`${count(sent)} sent again${refused > 0 ? `, ${count(refused)} could not be sent` : ''}`}
          description="A copy sent again that fails again stays a failure here, with its answer; one that gets through reads Redelivered."
          footer={
            <Button size="sm" variant="secondary" onClick={() => close(false)}>
              Done
            </Button>
          }
        >
          <div className="flex flex-col gap-4">{jobs}</div>
        </Modal>
      );
    }
    if (started.length > 0) {
      return (
        <Modal
          open={open}
          onOpenChange={close}
          title={`Redelivering ${copies(total)}`}
          description={
            <span className="font-mono text-micro text-faint">
              {started.map((job) => job.operationId).join(', ')} · runs on the server, safe to leave
              this page
            </span>
          }
          footer={
            <Button
              size="sm"
              variant="ghost"
              className="text-danger"
              disabled={stop.isSuccess}
              waiting={stop.isPending}
              waitingLabel="Stopping"
              onClick={() => stop.mutate()}
            >
              {stop.isSuccess ? 'Stops after this batch' : 'Stop after this batch'}
            </Button>
          }
        >
          <div className="flex flex-col gap-4">{jobs}</div>
          {stop.isError && <MutationError error={stop.error} />}
        </Modal>
      );
    }
    return (
      <Modal
        open={open}
        onOpenChange={close}
        title={`Redeliver ${copies(total)}`}
        description={`Every copy that failed ${where} in this window, sent again at the step it failed at by a server job${several ? ' — one job per flow' : ''}, in batches of up to 500.`}
        submit={{
          label: `Redeliver ${count(total)}`,
          onSubmit: () => start.mutate(),
          disabled: start.isPending,
          waiting: start.isPending,
          waitingLabel: 'Starting',
        }}
      >
        {failedPreview && (
          <ErrorState error={failedPreview.error} onRetry={() => void failedPreview.refetch()} />
        )}
        {several && (
          <ul className="flex flex-col gap-1">
            {counted.map(({ scope, total: flowTotal }) => (
              <li key={scope.flowId} className="flex items-baseline justify-between gap-3">
                <span className="truncate text-data text-primary">{flowName(scope.flowId)}</span>
                <span className="shrink-0 font-mono text-data text-muted">{count(flowTotal)}</span>
              </li>
            ))}
          </ul>
        )}
        {notes.map((note) => (
          <Note key={note}>{note}</Note>
        ))}
        {start.isError && <MutationError error={start.error} />}
      </Modal>
    );
  };

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        Redeliver {copies(total)}…
      </Button>
      {open && body()}
    </>
  );
};
